/**
 * AsistenciaAuditView — Auditoria de asistencia (solo lectura).
 *
 * Grilla colaboradores (filas) x dias del rango (columnas).
 * Colores por severidad:
 *   verde  = completo (4 marcajes OK)
 *   amarillo = tardanza o exceso de almuerzo
 *   naranja = incompleto_almuerzo (falto marcar almuerzo)
 *   rojo   = incompleto (sin salida final)
 *   gris   = descanso
 *   azul   = eventual_sin_plantilla
 *   tenue  = ausente
 *
 * Consume GET /api/v1/businesses/{bid}/asistencia/auditoria.
 * Complementa (no reemplaza) el chat con Argemiro: aca se ve la grilla, alla
 * se pregunta en lenguaje natural.
 *
 * @module presentation/views/AsistenciaAuditView
 */

import { AsistenciaApiClient } from '../../infrastructure/api/AsistenciaApiClient.js';
import { getSelectedBusiness, getSelectedBusinessName } from '../components/BusinessSelector.js';
import { errorMessage } from '../../domain/value-objects/CrudResult.js';
// IND-TARDANZA-UNA-T1 (A #20695): la tardanza que cuenta la decide la base (clasificacion del api 383); sin ella, la regla de antes.
import { GRACIA_MIN, TURNO_DUDOSO_MIN, tardanzaReal, esTurnoDudoso, traeClasificacion } from '../../domain/tardanzaAuditoria.js';
// TUR-T3: la entrada que la persona no marco se dice estimada, con palabras y el metodo (nunca igual que una marcada).
import { esEstimada, textoDeEntrada, contadorPorPersona, diasConEntradaEstimada, TITULO_DEL_CONTADOR, AYUDA_DEL_CONTADOR } from '../../application/asistencia/entradaEstimada.js';

const TZ = 'America/Bogota';

function hoyColombia() {
  const now = new Date();
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit'
  }).formatToParts(now);
  const y = parts.find(p => p.type === 'year').value;
  const m = parts.find(p => p.type === 'month').value;
  const d = parts.find(p => p.type === 'day').value;
  return `${y}-${m}-${d}`;
}

function addDays(iso, delta) {
  const d = new Date(iso + 'T12:00:00-05:00');
  d.setUTCDate(d.getUTCDate() + delta);
  return d.toISOString().slice(0, 10);
}

function labelFechaCorta(iso) {
  const d = new Date(iso + 'T12:00:00-05:00');
  const diaSemana = d.toLocaleDateString('es-CO', { weekday: 'short', timeZone: TZ });
  const diaMes = d.toLocaleDateString('es-CO', { day: '2-digit', month: 'short', timeZone: TZ });
  return `${diaSemana} ${diaMes}`;
}

/** Devuelve lista de YYYY-MM-DD entre desde y hasta (inclusive). */
function rangoDias(desde, hasta) {
  const out = [];
  let cur = desde;
  while (cur <= hasta) {
    out.push(cur);
    cur = addDays(cur, 1);
  }
  return out;
}

// ---------------------------------------------------------------------------
// REGLA DURA DE LOS TURNOS (VIS-T1 #3750, aprobada en el gate #3769)
//
// Un dia con una "tardanza" enorme casi nunca es una tardanza: es que la persona
// trabajo OTRO turno. Brayan entro 16:40 cuando su hora programada era 10:30 y el
// sistema lo conto como 371 minutos tarde. Sumado al resto, Argemiro llego a
// acusarlo de 892 minutos: falso y ofensivo. Con el corte aplicado son 5 tardanzas
// reales de 67 minutos + 4 dias a revisar aparte.
//
// Por eso los dias dudosos NUNCA se suman a las tardanzas. Van en un bloque propio.
// Los tres numeros son las decisiones (a) y (b) que propuse en #3750; el gate se
// aprobo sin corregirlas, asi que quedan como default. Cambiar el criterio es
// cambiar una constante, no reescribir la vista.
// ---------------------------------------------------------------------------
// GRACIA_MIN y TURNO_DUDOSO_MIN viven en domain/tardanzaAuditoria.js (y solo mandan si la base no clasifica el dia).
const QUINCENA_MIN_EXCESO_ALM = 60;    // decision (b): "fuera de rango" en quincena
const QUINCENA_MIN_DIAS_TARDE = 3;     // decision (b)


// El exceso de almuerzo se suma crudo, sin gracia: asi son las cifras que ya
// reporte en #3753 (Luis 601, Camilo 517, Duvan 195) y que Josse uso para decidir.
function excesoAlmuerzo(item) {
  return Number(item?.almuerzoExcedidoMin) || 0;
}

function sumaTardanza(items) { return (items || []).reduce((s, i) => s + tardanzaReal(i), 0); }
function sumaAlmuerzo(items) { return (items || []).reduce((s, i) => s + excesoAlmuerzo(i), 0); }
function diasConTardanza(items) { return (items || []).filter(i => tardanzaReal(i) > 0).length; }

const ESTADO_STYLE = {
  completo:                { bg: '#0d4f2b', fg: '#4fd87a', label: '✓' },
  incompleto:              { bg: '#5a1f1f', fg: '#f85149', label: '✗' },
  incompleto_almuerzo:     { bg: '#5a3a1f', fg: '#ff9f0a', label: '◑' },
  ausente:                 { bg: '#2a2a2a', fg: '#6e7681', label: '—' },
  descanso:                { bg: '#3a2f55', fg: '#b8a0e8', label: '💤' },
  eventual_sin_plantilla:  { bg: '#1f3a5a', fg: '#79c0ff', label: '?' },
};

// ---------------------------------------------------------------------------
// VIS-T2 parte 2: las 8 clasificaciones del detector (endpoint de Argy-A, #3843).
//
// La cuadricula ahora pinta con la clasificacion que YA viene decidida de la BD
// (fn_detectar_anomalias_asistencia). Antes clasificaba con mis heuristicas locales
// -- lo mejor posible sin detector, pero hoy seria una segunda verdad compitiendo
// con la de la BD sobre el mismo dia.
//
// Cada clasificacion tiene simbolo propio. TRABAJO_EN_DESCANSO es el caso que
// disparo la mision: 6 de los 7 dias de Yazmin. Antes se pintaba como "descanso"
// lila, indistinguible de un descanso normal, y por eso nadie lo veia.
// ---------------------------------------------------------------------------
const CLASIF_STYLE = {
  OK:                  { bg: '#0d4f2b', fg: '#4fd87a', label: '✓', nombre: 'A tiempo' },
  TARDANZA:            { bg: '#5a3a1f', fg: '#ff9f0a', label: '⚠', nombre: 'Tardanza' },
  TRABAJO_EN_DESCANSO: { bg: '#5c2d91', fg: '#d0b3ff', label: '☕', nombre: 'Trabajó en su descanso' },
  POSIBLE_INTERCAMBIO: { bg: '#1f3a5a', fg: '#79c0ff', label: '⇄', nombre: 'Posible cambio de turno' },
  MARCAJE_SOSPECHOSO:  { bg: '#5a1f4a', fg: '#ff8ad8', label: '?', nombre: 'Marcaje sospechoso' },
  DIA_INCOMPLETO:      { bg: '#5a1f1f', fg: '#f85149', label: '✗', nombre: 'Día incompleto' },
  DIA_EN_CURSO:        { bg: '#1f4a4a', fg: '#5ce0d8', label: '…', nombre: 'Día en curso' },
  FUERA_DE_RANGO:      { bg: '#4a3a1f', fg: '#e3b341', label: '↔', nombre: 'Fuera de rango' },
};

/**
 * Celda pintada con la clasificacion del detector.
 *
 * `detalle` se muestra TAL CUAL: viene en lenguaje humano desde la BD y reescribirlo
 * aca seria inventar una segunda explicacion. `turnoQueCalza` va PEGADO al desvio,
 * no aparte: un desvio de 371 minutos sin saber con quien calza el turno solo asusta
 * -- es exactamente lo que hizo que Argemiro acusara a Brayan de 892 minutos.
 */
function celdaClasificada(anom) {
  const style = CLASIF_STYLE[anom.clasificacion] || { bg: '#2a2a2a', fg: '#6e7681', label: '·', nombre: anom.clasificacion };
  const desvio = Number(anom.desvioMin) || 0;

  const tooltip = [
    `${style.nombre}`,
    anom.entradaProgramada ? `programada ${anom.entradaProgramada}` : 'sin horario programado',
    anom.entradaReal ? `real ${anom.entradaReal}` : 'no marcó entrada',
    desvio !== 0 ? `desvío ${desvio}m` : null,
    anom.turnoQueCalza ? `calza con: ${anom.turnoQueCalza}` : null,
    anom.detalle || null
  ].filter(Boolean).join(' · ');

  // Solo se rotula el numero cuando es una TARDANZA real. En las demas
  // clasificaciones el minuto no es la noticia y ponerlo lo vuelve a leer como retraso.
  const rotulo = anom.clasificacion === 'TARDANZA' && desvio > 0 ? ` ${desvio}m` : '';

  return `<div class="audit-cell" title="${escAttr(tooltip)}"
    style="background:${style.bg};color:${style.fg};display:flex;align-items:center;justify-content:center;
    min-height:36px;font-weight:600;font-size:0.875rem;border-radius:4px">
    ${style.label}${rotulo}
  </div>`;
}

function celdaEstado(fila) {
  const estado = fila?.estadoDia || 'ausente';
  let style = ESTADO_STYLE[estado] || ESTADO_STYLE.ausente;

  const tarde = tardanzaReal(fila);
  const dudoso = esTurnoDudoso(fila);
  const hayExcesoAlm = excesoAlmuerzo(fila) > GRACIA_MIN;

  // Un dia de turno distinto no puede pintarse como una tardanza gigante: es el error
  // que esta vista existe para corregir. Va con su propio color y su propio simbolo.
  if (dudoso) {
    style = { bg: '#1f3a5a', fg: '#79c0ff', label: '⇄' };
  } else if (estado === 'completo' && (tarde > 0 || hayExcesoAlm)) {
    style = { bg: '#5a3a1f', fg: '#ff9f0a', label: '⚠' };
  }

  const tooltip = [
    textoDeEntrada(fila) || null,
    fila?.horaSalidaReal ? `salida ${fila.horaSalidaReal}` : null,
    dudoso ? (traeClasificacion(fila) ? `${fila.clasificacion === 'POSIBLE_INTERCAMBIO' ? 'posible cambio de turno' : 'fuera del rango del turno'} (${fila.tardanzaEntradaMin}m de desvio) — no cuenta como tardanza` : `horario distinto al programado (${fila.tardanzaEntradaMin}m de desvio) — no cuenta como tardanza`) : null,
    tarde > 0 ? `tarde ${tarde}m` : null,
    hayExcesoAlm ? `+${fila.almuerzoExcedidoMin}m almuerzo` : null,
    fila?.horasEfectivas != null ? `${fila.horasEfectivas}h` : null,
    `estado: ${estado}`
  ].filter(Boolean).join(' · ');

  return `<div class="audit-cell" title="${escAttr(tooltip)}"
    style="background:${style.bg};color:${style.fg};display:flex;align-items:center;justify-content:center;
    min-height:36px;font-weight:600;font-size:0.875rem;border-radius:4px">
    ${style.label}${tarde > 0 ? ` ${tarde}m` : ''}${esEstimada(fila) ? '<span data-audit-estimada style="font-size:0.875rem;font-weight:400;margin-left:4px;font-style:italic">estimada</span>' : ''}
  </div>`;
}

function celdaVacia() {
  return `<div class="audit-cell"
    style="background:#161b22;min-height:36px;display:flex;align-items:center;justify-content:center;
    color:#30363d;font-size:0.875rem">—</div>`;
}

// VIS-T2 (bit #3771): paleta estable por persona. El color debe significar LA MISMA
// persona en el grafico apilado y en la leyenda; si se reasignara por orden de
// aparicion, cambiaria al mover el rango de fechas y el lector perderia la referencia.
const COLORES_PERSONA = ['#58a6ff', '#f85149', '#3fb950', '#d29922', '#a371f7', '#ff9f0a', '#79c0ff', '#db61a2'];
function colorDePersona(nombre, indice) {
  return COLORES_PERSONA[indice % COLORES_PERSONA.length];
}

/**
 * VIS-T2 vista TEMPORAL (por defecto): eje X = dias, eje Y = minutos de tardanza
 * acumulados ese dia, apilados con un color por persona.
 *
 * Se dibuja con divs, no con una libreria de graficos: son pocas barras, el panel no
 * carga ninguna libreria de charts hoy, y meter una por esto seria peso nuevo en el
 * bundle para algo que resuelve un flex.
 */
function renderChartTemporal($host, dias, porColab, nombresOrdenados) {
  // minutos de tardanza por (dia, persona) — REAL: sin gracia y sin turnos distintos
  const porDia = dias.map(d => {
    const segmentos = nombresOrdenados.map((nombre, idx) => {
      const items = porColab.get(nombre) || [];
      const delDia = items.find(i => i.fechaCol?.slice(0, 10) === d);
      return { nombre, idx, min: tardanzaReal(delDia) };
    }).filter(s => s.min > 0);
    return { dia: d, segmentos, total: segmentos.reduce((s, x) => s + x.min, 0) };
  });

  const maxTotal = Math.max(...porDia.map(d => d.total), 1);
  const hayAlgo = porDia.some(d => d.total > 0);

  if (!hayAlgo) {
    $host.innerHTML = `<div style="padding:18px;text-align:center;color:#3fb950;font-size:0.875rem">
      &#10003; Sin tardanzas en el rango. Nadie llegó tarde.</div>`;
    return;
  }

  const barras = porDia.map(d => {
    const alturaTotal = Math.round((d.total / maxTotal) * 120);
    const pilas = d.segmentos.map(s => {
      const h = d.total > 0 ? Math.max(2, Math.round((s.min / d.total) * alturaTotal)) : 0;
      return `<div title="${escAttr(s.nombre)}: ${s.min} min" style="height:${h}px;background:${colorDePersona(s.nombre, s.idx)}"></div>`;
    }).join('');
    return `
      <div style="display:flex;flex-direction:column;align-items:center;gap:4px;flex:1;min-width:34px">
        <div style="font-size:0.875rem;color:${d.total > 0 ? '#d29922' : '#30363d'};height:12px">${d.total > 0 ? d.total + 'm' : ''}</div>
        <div style="display:flex;flex-direction:column-reverse;width:100%;max-width:38px;border-radius:3px 3px 0 0;overflow:hidden">${pilas}</div>
        <div style="font-size:0.875rem;color:#8b949e;white-space:nowrap">${labelFechaCorta(d.dia)}</div>
      </div>`;
  }).join('');

  const leyenda = nombresOrdenados.map((nombre, idx) => {
    const total = sumaTardanza(porColab.get(nombre));
    if (total === 0) return '';
    return `<span style="display:inline-flex;align-items:center;gap:4px;font-size:0.875rem;color:#8b949e">
      <span style="width:9px;height:9px;border-radius:2px;background:${colorDePersona(nombre, idx)}"></span>${escHtmlAudit(nombre.split(' ')[0])} ${total}m</span>`;
  }).filter(Boolean).join('');

  $host.innerHTML = `
    <div style="display:flex;align-items:flex-end;gap:6px;height:160px">${barras}</div>
    <div style="display:flex;gap:10px;flex-wrap:wrap;margin-top:10px;padding-top:8px;border-top:1px solid #21262d">${leyenda}</div>`;
}

/**
 * VIS-T2 vista ACUMULADA (alterna): una barra por PERSONA, partida en tardanza de
 * entrada (A) y exceso de almuerzo (B). Responde "quien", no "cuando".
 *
 * @param {boolean} soloFueraDeRango - plantilla QUINCENA: aparecer ES la senal.
 */
function renderChartAcumulado($host, porColab, nombresOrdenados, soloFueraDeRango = false) {
  let filas = nombresOrdenados.map(nombre => {
    const items = porColab.get(nombre) || [];
    const tardanza = sumaTardanza(items);
    const almuerzo = sumaAlmuerzo(items);
    return { nombre, tardanza, almuerzo, total: tardanza + almuerzo, diasTarde: diasConTardanza(items) };
  }).filter(f => f.total > 0);

  // QUINCENA (#3750): "solo acumulado por persona y SOLO quien esta fuera de rango.
  // Aparecer ES la senal". Sobre 15 dias todo el mundo acumula algo; sin este filtro
  // la lista vuelve a ser ruido y el problema real se pierde entre los normales.
  const totalAntesDelFiltro = filas.length;
  if (soloFueraDeRango) {
    filas = filas.filter(f => f.almuerzo > QUINCENA_MIN_EXCESO_ALM || f.diasTarde > QUINCENA_MIN_DIAS_TARDE);
  }
  filas.sort((a, b) => b.total - a.total); // peor primero: el problema arriba

  if (filas.length === 0) {
    $host.innerHTML = `<div style="padding:18px;text-align:center;color:#3fb950;font-size:0.875rem">
      &#10003; ${soloFueraDeRango
        ? `Nadie fuera de rango. ${totalAntesDelFiltro} persona${totalAntesDelFiltro === 1 ? '' : 's'} con desvíos menores, dentro de lo tolerado.`
        : 'Sin tardanzas ni excesos de almuerzo en el rango.'}</div>`;
    return;
  }

  const max = Math.max(...filas.map(f => f.total), 1);
  const barras = filas.map(f => `
    <div style="display:grid;grid-template-columns:150px 1fr 90px;gap:8px;align-items:center;margin-bottom:6px">
      <div style="font-size:0.875rem;color:#c9d1d9;overflow:hidden;text-overflow:ellipsis;white-space:nowrap" title="${escAttr(f.nombre)}">${escHtmlAudit(f.nombre)}</div>
      <div style="display:flex;height:18px;border-radius:3px;overflow:hidden;background:#161b22">
        ${f.tardanza > 0 ? `<div title="entrada: ${f.tardanza} min" style="width:${(f.tardanza / max) * 100}%;background:#d29922"></div>` : ''}
        ${f.almuerzo > 0 ? `<div title="almuerzo: ${f.almuerzo} min" style="width:${(f.almuerzo / max) * 100}%;background:#f85149"></div>` : ''}
      </div>
      <div style="font-size:0.875rem;color:#8b949e;text-align:right">${f.tardanza > 0 ? `${f.tardanza}m entr` : ''}${f.tardanza > 0 && f.almuerzo > 0 ? ' · ' : ''}${f.almuerzo > 0 ? `${f.almuerzo}m alm` : ''}</div>
    </div>`).join('');

  const nota = soloFueraDeRango
    ? `<div style="margin-top:8px;font-size:0.875rem;color:#6e7681">Solo se listan los que superan ${QUINCENA_MIN_EXCESO_ALM}m de almuerzo acumulado o más de ${QUINCENA_MIN_DIAS_TARDE} días con tardanza. ${totalAntesDelFiltro - filas.length} persona(s) quedaron fuera por estar dentro de lo tolerado.</div>`
    : '';

  $host.innerHTML = `${barras}
    <div style="display:flex;gap:12px;margin-top:8px;padding-top:8px;border-top:1px solid #21262d;font-size:0.875rem;color:#8b949e">
      <span><span style="display:inline-block;width:9px;height:9px;background:#d29922;border-radius:2px"></span> entrada tarde</span>
      <span><span style="display:inline-block;width:9px;height:9px;background:#f85149;border-radius:2px"></span> exceso de almuerzo</span>
    </div>${nota}`;
}

/**
 * Plantilla 1_DIA (#3750): lista corta, una linea por persona: nombre + hora de
 * entrada + veredicto. Unico periodo donde el evento individual es la unidad
 * correcta, y por eso el unico SIN toggle: no hay nada que acumular en un solo dia.
 */
function renderListaDia($host, porColab, nombresOrdenados) {
  const filas = nombresOrdenados.map(nombre => {
    const it = (porColab.get(nombre) || [])[0] || null;
    const tarde = tardanzaReal(it);
    const dudoso = esTurnoDudoso(it);
    const estado = it?.estadoDia || 'ausente';

    let veredicto, color;
    if (!it || estado === 'ausente') { veredicto = 'sin registro'; color = '#6e7681'; }
    else if (estado === 'descanso') { veredicto = 'descanso'; color = '#b8a0e8'; }
    else if (dudoso) { veredicto = 'horario distinto al programado — revisar'; color = '#79c0ff'; }
    else if (tarde > 0) { veredicto = `${tarde} min tarde`; color = '#ff9f0a'; }
    else { veredicto = 'a tiempo'; color = '#3fb950'; }

    const alm = excesoAlmuerzo(it);
    return `
      <div style="display:grid;grid-template-columns:1fr 90px 1fr;gap:10px;align-items:center;padding:7px 4px;border-bottom:1px solid #21262d">
        <div style="font-size:0.875rem;color:#c9d1d9;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${escHtmlAudit(nombre)}</div>
        <div style="font-size:0.875rem;color:#8b949e;font-variant-numeric:tabular-nums"${esEstimada(it) ? ` data-audit-estimada title="${escAttr(textoDeEntrada(it))}"` : ''}>${it?.horaEntradaReal ? escHtmlAudit(it.horaEntradaReal) : '—'}${esEstimada(it) ? '<div style="font-style:italic">estimada</div>' : ''}</div>
        <div style="font-size:0.875rem;color:${color}">${veredicto}${alm > GRACIA_MIN ? `<span style="color:#8b949e"> · +${alm}m almuerzo</span>` : ''}</div>
      </div>`;
  }).join('');

  $host.innerHTML = filas || `<div style="padding:18px;text-align:center;color:#8b949e;font-size:0.875rem">Sin registros del día.</div>`;
}

/**
 * Plantilla 4_MES (#3750): tendencia, no dias. Una linea por persona: mejora /
 * empeora / estable, comparando la primera mitad del periodo contra la segunda.
 * "El numero exacto solo si lo pide" -> los minutos van en el tooltip.
 *
 * 30 barras diarias no se leen; lo que el dueño necesita del mes es la direccion.
 */
function renderTendencia($host, dias, porColab, nombresOrdenados) {
  if (dias.length < 4) {
    $host.innerHTML = `<div style="padding:18px;text-align:center;color:#8b949e;font-size:0.875rem">
      La tendencia necesita al menos 4 días para partir el periodo en dos mitades.</div>`;
    return;
  }
  const corte = Math.floor(dias.length / 2);
  const primeraMitad = new Set(dias.slice(0, corte));

  const filas = nombresOrdenados.map(nombre => {
    const items = porColab.get(nombre) || [];
    let a = 0, b = 0;
    for (const it of items) {
      const d = it.fechaCol?.slice(0, 10);
      const min = tardanzaReal(it) + excesoAlmuerzo(it);
      if (primeraMitad.has(d)) a += min; else b += min;
    }
    return { nombre, a, b, delta: b - a };
  }).filter(f => f.a > 0 || f.b > 0)
    .sort((x, y) => y.delta - x.delta); // el que mas empeoro, arriba

  if (filas.length === 0) {
    $host.innerHTML = `<div style="padding:18px;text-align:center;color:#3fb950;font-size:0.875rem">
      &#10003; Sin desvíos en el periodo: no hay tendencia que mostrar.</div>`;
    return;
  }

  // "Estable" no puede ser delta exactamente 0 o nadie caeria ahi. Un cambio menor a
  // 15 min en media quincena es ruido, no una tendencia.
  const UMBRAL_ESTABLE = 15;
  const cuerpo = filas.map(f => {
    let icono, texto, color;
    if (f.delta > UMBRAL_ESTABLE) { icono = '▲'; texto = 'empeora'; color = '#f85149'; }
    else if (f.delta < -UMBRAL_ESTABLE) { icono = '▼'; texto = 'mejora'; color = '#3fb950'; }
    else { icono = '='; texto = 'estable'; color = '#8b949e'; }
    return `
      <div title="1ra mitad ${f.a}m · 2da mitad ${f.b}m" style="display:grid;grid-template-columns:1fr 130px;gap:10px;align-items:center;padding:7px 4px;border-bottom:1px solid #21262d">
        <div style="font-size:0.875rem;color:#c9d1d9;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${escHtmlAudit(f.nombre)}</div>
        <div style="font-size:0.875rem;color:${color};font-weight:600">${icono} ${texto}</div>
      </div>`;
  }).join('');

  $host.innerHTML = `${cuerpo}
    <div style="margin-top:8px;font-size:0.875rem;color:#6e7681">Compara la primera mitad del periodo contra la segunda (tardanza + exceso de almuerzo). Pasá el mouse para ver los minutos.</div>`;
}

/**
 * REGLA DURA (#3750): los dias de turno distinto van en bloque aparte al final,
 * NUNCA sumados a las tardanzas. Es literalmente lo que hizo que Argemiro acusara a
 * Brayan de 371 minutos tarde.
 */
function renderBloqueTurnos($host, porColab, nombresOrdenados) {
  const dudosos = [];
  for (const nombre of nombresOrdenados) {
    for (const it of (porColab.get(nombre) || [])) {
      if (esTurnoDudoso(it)) dudosos.push({ nombre, it });
    }
  }
  if (dudosos.length === 0) { $host.innerHTML = ''; return; }

  const lineas = dudosos.map(({ nombre, it }) => `
    <div style="font-size:0.875rem;color:#8b949e;padding:3px 0">
      <span style="color:#c9d1d9">${escHtmlAudit(nombre)}</span> ·
      ${escHtmlAudit(labelFechaCorta(it.fechaCol?.slice(0, 10)))} ·
      ${esEstimada(it) ? escHtmlAudit(textoDeEntrada(it)) : `entró ${escHtmlAudit(it.horaEntradaReal || '?')}`} (${it.tardanzaEntradaMin}m de desvío)
    </div>`).join('');

  $host.innerHTML = `
    <div style="border:1px solid #1f3a5a;border-left:3px solid #79c0ff;border-radius:6px;padding:10px 12px;background:#0d1117">
      <div style="font-size:0.875rem;color:#79c0ff;font-weight:600;margin-bottom:6px">
        ⇄ ${dudosos.length} día${dudosos.length === 1 ? '' : 's'} con horario distinto al programado — revisar
      </div>
      <div style="font-size:0.875rem;color:#6e7681;margin-bottom:6px">
        ${dudosos.some(({ it }) => traeClasificacion(it))
          ? 'La base los clasificó como posible cambio de turno o fuera del rango del turno. NO se cuentan como tardanza.'
          : `Desvíos de ${TURNO_DUDOSO_MIN} minutos o más. NO se cuentan como tardanza: a esa magnitud casi siempre
        es un cambio de turno que no quedó registrado, no alguien que llegó tarde.`}
      </div>
      ${lineas}
    </div>`;
}

// Deuda 4: era una copia del escape; ahora es el compartido.
import { esc as escHtmlAudit } from '../shared/texto.js';
const escAttr = (s) => escHtmlAudit(s);

function fmtResumenColab(itemsColab) {
  const total = sumaTardanza(itemsColab);          // sin gracia y sin turnos distintos
  const excesoAlm = sumaAlmuerzo(itemsColab);
  const dudosos = itemsColab.filter(esTurnoDudoso).length;
  const horas = itemsColab.reduce((s, i) => s + Number(i.horasEfectivas ?? 0), 0);
  return `
    <div style="font-size:0.875rem;color:#8b949e;margin-top:2px">
      ${total > 0 ? `<span style="color:#ff9f0a">${total}m tarde</span> · ` : ''}
      ${excesoAlm > 0 ? `<span style="color:#ff9f0a">+${excesoAlm}m alm</span> · ` : ''}
      ${dudosos > 0 ? `<span style="color:#79c0ff">${dudosos}d a revisar</span> · ` : ''}
      <span>${horas.toFixed(1)}h efectivas</span>
    </div>`;
}

// ---------------------------------------------------------------------------
// PLANTILLAS POR PERIODO (VIS-T1 #3750 + cambio de alcance de Josse en #3769)
//
// Cada periodo tiene DOS vistas intercambiables y la temporal es la por defecto,
// salvo DIA: en un solo dia no hay nada que acumular ni ninguna tendencia que ver,
// asi que no lleva toggle.
// ---------------------------------------------------------------------------
// El endpoint del detector acota a 92 dias por consulta (#3843). Las plantillas
// (1/7/15/30) entran holgadas; el RANGO LIBRE es el unico que puede pasarse.
const LIMITE_DIAS_DETECTOR = 92;

/**
 * Resumen por clasificacion. Se pinta con `resumenPorClasificacion` TAL CUAL viene:
 * el endpoint ya lo trae contado y reagruparlo aca seria arriesgar que el resumen y
 * la cuadricula digan numeros distintos del mismo rango.
 */
/** TUR-T3: Dias con entrada estimada este periodo, por persona y de mayor a menor. Vacio si nadie tuvo. */
function pintarEstimadas($host, payload) {
  if (!$host) return;
  const filas = contadorPorPersona(payload);
  if (filas.length === 0) { $host.innerHTML = ''; return; }
  $host.innerHTML = `
    <div data-audit-contador-estimadas style="border:1px solid #30363d;border-left:3px solid #d2a8ff;border-radius:6px;padding:10px 12px;background:#0d1117">
      <div style="font-size:0.875rem;color:#d2a8ff;font-weight:600">${escHtmlAudit(TITULO_DEL_CONTADOR)}</div>
      <div style="font-size:0.875rem;color:#8b949e;margin:2px 0 6px">${escHtmlAudit(AYUDA_DEL_CONTADOR)}</div>
      ${filas.map((f) => `<div style="display:flex;justify-content:space-between;gap:12px;font-size:0.875rem;padding:3px 0;border-bottom:1px solid #21262d">
        <span style="color:#c9d1d9;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${escHtmlAudit(f.nombre)}</span>
        <span style="color:#c9d1d9;font-variant-numeric:tabular-nums">${f.dias} ${f.dias === 1 ? 'día' : 'días'}</span></div>`).join('')}
    </div>`;
}

function renderResumenClasificaciones($host, resumen) {
  const entradas = Object.entries(resumen || {}).filter(([, n]) => Number(n) > 0);
  if (entradas.length === 0) { $host.innerHTML = ''; return; }

  // OK al final: la noticia son las novedades, no los dias normales.
  entradas.sort((a, b) => (a[0] === 'OK' ? 1 : b[0] === 'OK' ? -1 : b[1] - a[1]));

  const chips = entradas.map(([clave, n]) => {
    const s = CLASIF_STYLE[clave] || { bg: '#2a2a2a', fg: '#6e7681', label: '·', nombre: clave };
    return `<span title="${escAttr(s.nombre)}" style="display:inline-flex;align-items:center;gap:5px;
      padding:4px 9px;border-radius:12px;background:${s.bg};color:${s.fg};font-size:0.875rem;font-weight:600">
      ${s.label} ${escHtmlAudit(s.nombre)} <span style="opacity:.8">${n}</span></span>`;
  }).join('');

  const conNovedad = entradas.filter(([k]) => k !== 'OK').reduce((s, [, n]) => s + Number(n), 0);
  $host.innerHTML = `
    <div style="display:flex;gap:6px;flex-wrap:wrap;align-items:center">
      <span style="font-size:0.875rem;color:#8b949e;margin-right:2px">
        ${conNovedad > 0 ? `${conNovedad} día(s) con novedad:` : 'Sin novedades en el rango:'}
      </span>${chips}
    </div>`;
}

const PLANTILLAS = {
  dia:      { label: 'Hoy',      dias: 1,  toggle: false, alterna: null,        hintA: 'Entrada de cada persona hoy' },
  semana:   { label: 'Semana',   dias: 7,  toggle: true,  alterna: 'acumulado', hintA: 'Tardanzas por día · dónde se concentra el problema', hintB: 'Acumulado por persona · peor primero', labelB: 'Por persona' },
  quincena: { label: 'Quincena', dias: 15, toggle: true,  alterna: 'acumulado', hintA: 'Tardanzas por día · dónde se concentra el problema', hintB: 'Solo quien está fuera de rango · aparecer es la señal', labelB: 'Fuera de rango', filtrarFueraDeRango: true },
  mes:      { label: 'Mes',      dias: 30, toggle: true,  alterna: 'tendencia', hintA: 'Tardanzas por día · dónde se concentra el problema', hintB: 'Tendencia · primera mitad del mes contra la segunda', labelB: 'Tendencia' },
};

/**
 * Plantilla 5_RANGO_LIBRE (#3750): no tiene forma propia, adopta la del periodo que
 * mas se le parece por duracion. Un rango de 12 dias se lee como una quincena.
 */
function plantillaPorDuracion(desde, hasta) {
  const n = rangoDias(desde, hasta).length;
  if (n <= 1) return 'dia';
  if (n <= 7) return 'semana';
  if (n <= 20) return 'quincena';
  return 'mes';
}

export async function renderAsistenciaAudit(container) {
  const businessId = getSelectedBusiness();
  const businessName = getSelectedBusinessName();

  if (!businessId) {
    container.innerHTML = `
      <div class="view-header"><h2 class="view-title">Auditoria de Asistencia</h2></div>
      <div class="empty-state"><p>Selecciona un negocio.</p></div>`;
    return;
  }

  const hoy = hoyColombia();
  let desde = addDays(hoy, -6);  // ultimos 7 dias
  let hasta = hoy;

  container.innerHTML = `
    <div class="view-header">
      <div>
        <h2 class="view-title">Auditoria de Asistencia</h2>
        <div class="business-scope-banner" style="margin-top:0.4rem">
          <span class="business-scope-icon">🏢</span>
          <span class="business-scope-label">Negocio activo:</span>
          <span class="business-scope-name">${businessName}</span>
          <span class="business-scope-hint">Vista solo-lectura · usa Argemiro para preguntas conversacionales</span>
        </div>
      </div>
      <div style="display:flex;gap:0.5rem;flex-wrap:wrap;align-items:end">
        <div>
          <label style="font-size:0.875rem;color:#8b949e">Desde</label>
          <input type="date" id="audit-desde" class="form-control" value="${desde}" style="min-width:140px;font-size:14px">
        </div>
        <div>
          <label style="font-size:0.875rem;color:#8b949e">Hasta</label>
          <input type="date" id="audit-hasta" class="form-control" value="${hasta}" style="min-width:140px;font-size:14px">
        </div>
        <button class="btn btn-primary btn-sm" id="audit-buscar">Buscar</button>
        <a href="#/asistencia" class="btn btn-ghost btn-sm">← Asistencia</a>
      </div>
    </div>

    <!-- VIS-T2: las plantillas de periodo (#3750). Cada una cambia el rango Y la forma
         de leerlo; no es solo un atajo de fechas. -->
    <div style="display:flex;gap:6px;flex-wrap:wrap;margin-bottom:12px">
      ${Object.entries(PLANTILLAS).map(([k, p]) => `
        <button type="button" class="audit-periodo-btn" data-periodo="${k}"
          style="padding:5px 14px;font-size:0.875rem;border:1px solid #30363d;border-radius:6px;cursor:pointer;
          background:${k === 'semana' ? '#1f6feb' : 'transparent'};color:${k === 'semana' ? '#fff' : '#8b949e'}">${p.label}</button>`).join('')}
      <span id="audit-periodo-libre" style="align-self:center;font-size:0.875rem;color:#6e7681"></span>
    </div>

    <div class="ops-att-grid" id="audit-kpis" style="margin-bottom:12px"></div>

    <!-- VIS-T2 (bit #3771): las 2 vistas del periodo con toggle. La TEMPORAL es la
         por defecto: poner los dias en el eje muestra en QUE dia se concentra el
         problema, cosa que el acumulado por persona esconde. El acumulado sigue a un
         clic para cuando la pregunta es "quien", no "cuando". -->
    <div style="display:flex;align-items:center;justify-content:space-between;gap:10px;flex-wrap:wrap;margin-bottom:8px">
      <div style="font-size:0.875rem;color:#8b949e" id="audit-vista-hint">Tardanzas por día · dónde se concentra el problema</div>
      <div id="audit-vista-toggle" style="display:flex;gap:0;border:1px solid #30363d;border-radius:6px;overflow:hidden">
        <button type="button" class="audit-vista-btn" data-vista="temporal"
                style="padding:5px 12px;font-size:0.875rem;background:#1f6feb;color:#fff;border:0;cursor:pointer">Por día</button>
        <button type="button" class="audit-vista-btn" data-vista="alterna"
                style="padding:5px 12px;font-size:0.875rem;background:transparent;color:#8b949e;border:0;cursor:pointer">Por persona</button>
      </div>
    </div>

    <!-- overflow-x:auto NO es cosmetico. Con la plantilla MES son 30 barras a min-width
         34px = ~1200px: sin esto el grafico empuja el BODY y en un telefono de 375px la
         pagina entera scrollea en horizontal, no solo el grafico. Medido: body.scrollWidth
         1217 contra clientWidth 375. La cuadricula de abajo ya scrolleaba dentro de si
         misma; al grafico se me paso. -->
    <div id="audit-chart" style="overflow-x:auto;border:1px solid #30363d;border-radius:6px;padding:12px;margin-bottom:12px;background:#0d1117"></div>

    <div id="audit-resumen-clasif" style="margin-bottom:12px"></div>
    <div id="audit-estimadas" style="margin-bottom:12px"></div>

    <!-- Si el detector falla, este aviso aparece SI o SI. La cuadricula sigue
         funcionando con las heuristicas locales, pero quien mira tiene que saber que
         esta viendo el respaldo y no la clasificacion de la BD. Degradar en silencio
         a la version vieja es el fail-silent que prohibe la REGLA_DURA_1 del #3795. -->
    <div id="audit-detector-aviso" style="margin-bottom:12px"></div>

    <div id="audit-turnos" style="margin-bottom:12px"></div>

    <div id="audit-grid-wrap" style="overflow-x:auto;border:1px solid #30363d;border-radius:6px">
      <div class="loading-state">Cargando auditoria…</div>
    </div>

    <!-- Leyenda generada del mismo mapa que pinta las celdas: si se agrega una
         clasificacion, no hay que acordarse de actualizar la leyenda a mano. -->
    <div style="margin-top:10px;font-size:0.875rem;color:#6e7681;display:flex;gap:12px;flex-wrap:wrap">
      ${Object.values(CLASIF_STYLE).map(s => `
        <span><span style="display:inline-block;width:10px;height:10px;background:${s.bg};border-radius:2px"></span> ${s.label} ${escHtmlAudit(s.nombre)}</span>`).join('')}
      <span><span style="display:inline-block;width:10px;height:10px;background:#3a2f55;border-radius:2px"></span> 💤 descanso tomado</span>
      <span><span style="display:inline-block;width:10px;height:10px;background:#2a2a2a;border-radius:2px"></span> — sin registro</span>
    </div>`;

  // VIS-T2 (bit #3771): estado de la vista activa + datos de los graficos.
  // 'temporal' por defecto: la pregunta primero es CUANDO, no quien.
  let vistaActiva = 'temporal';
  let periodoActivo = 'semana';
  let porNombre = new Map();
  let nombresOrden = [];
  let diasRango = [];

  function pintarChart() {
    const $chart = document.getElementById('audit-chart');
    const $hint = document.getElementById('audit-vista-hint');
    const $toggle = document.getElementById('audit-vista-toggle');
    const $turnos = document.getElementById('audit-turnos');
    if (!$chart) return;

    // El rango libre no tiene forma propia: adopta la del periodo que mas se le
    // parece por duracion (#3750, plantilla 5).
    const clave = periodoActivo === 'libre' ? plantillaPorDuracion(desde, hasta) : periodoActivo;
    const p = PLANTILLAS[clave] || PLANTILLAS.semana;

    if ($toggle) $toggle.style.display = p.toggle ? 'flex' : 'none';
    if (!p.toggle) vistaActiva = 'temporal';

    // El boton alterno cambia de nombre segun el periodo: en quincena filtra, en mes
    // muestra tendencia. Un boton que dice siempre lo mismo mentiria sobre lo que hace.
    const $btnAlterna = container.querySelector('.audit-vista-btn[data-vista="alterna"]');
    if ($btnAlterna && p.labelB) $btnAlterna.textContent = p.labelB;

    if (porNombre.size === 0) { $chart.innerHTML = ''; if ($turnos) $turnos.innerHTML = ''; return; }

    if (clave === 'dia') {
      renderListaDia($chart, porNombre, nombresOrden);
      if ($hint) $hint.textContent = p.hintA;
    } else if (vistaActiva === 'temporal') {
      renderChartTemporal($chart, diasRango, porNombre, nombresOrden);
      if ($hint) $hint.textContent = p.hintA;
    } else if (p.alterna === 'tendencia') {
      renderTendencia($chart, diasRango, porNombre, nombresOrden);
      if ($hint) $hint.textContent = p.hintB;
    } else {
      renderChartAcumulado($chart, porNombre, nombresOrden, !!p.filtrarFueraDeRango);
      if ($hint) $hint.textContent = p.hintB;
    }

    // El bloque de turnos distintos va SIEMPRE, en cualquier plantilla: es la regla
    // dura, no un adorno de una vista.
    if ($turnos) renderBloqueTurnos($turnos, porNombre, nombresOrden);
  }

  function aplicarPeriodo(clave) {
    periodoActivo = clave;
    const p = PLANTILLAS[clave];
    if (p) {
      hasta = hoyColombia();
      desde = addDays(hasta, -(p.dias - 1));
      document.getElementById('audit-desde').value = desde;
      document.getElementById('audit-hasta').value = hasta;
    } else {
      // rango libre: las fechas mandan, hay que leerlas ANTES de deducir la plantilla
      desde = document.getElementById('audit-desde').value || desde;
      hasta = document.getElementById('audit-hasta').value || hasta;
    }
    container.querySelectorAll('.audit-periodo-btn').forEach(b => {
      const activo = b.dataset.periodo === clave;
      b.style.background = activo ? '#1f6feb' : 'transparent';
      b.style.color = activo ? '#fff' : '#8b949e';
    });
    const $libre = document.getElementById('audit-periodo-libre');
    if ($libre) {
      $libre.textContent = clave === 'libre'
        ? `rango libre · se lee como ${PLANTILLAS[plantillaPorDuracion(desde, hasta)].label.toLowerCase()}`
        : '';
    }
    vistaActiva = 'temporal';
    container.querySelectorAll('.audit-vista-btn').forEach(b => {
      const activo = b.dataset.vista === 'temporal';
      b.style.background = activo ? '#1f6feb' : 'transparent';
      b.style.color = activo ? '#fff' : '#8b949e';
    });
    load();
  }

  async function load() {
    const wrap = document.getElementById('audit-grid-wrap');
    const kpis = document.getElementById('audit-kpis');
    if (!wrap || !kpis) return;
    wrap.innerHTML = '<div class="loading-state" style="padding:20px">Cargando…</div>';
    kpis.innerHTML = '';

    desde = document.getElementById('audit-desde').value || desde;
    hasta = document.getElementById('audit-hasta').value || hasta;

    // Las dos llamadas van en paralelo: son independientes y encadenarlas solo suma
    // espera. auditoria() manda los minutos y el detector manda la clasificacion.
    const nDias = rangoDias(desde, hasta).length;
    const [res, resAnom] = await Promise.all([
      AsistenciaApiClient.auditoria(businessId, { desde, hasta }),
      nDias <= LIMITE_DIAS_DETECTOR
        ? AsistenciaApiClient.anomalias(businessId, { desde, hasta })
        : Promise.resolve({ ok: false, error: `El detector acepta hasta ${LIMITE_DIAS_DETECTOR} días y el rango pedido tiene ${nDias}. Acortá el rango para ver las clasificaciones.`, code: 'RANGO_EXCEDIDO' })
    ]);

    if (!res.ok) {
      // Escapado: el mensaje viene del backend (data.mensaje/message/detail) y algunos
      // endpoints devuelven texto que incluye lo que tecleo el usuario.
      // errorMessage() es el helper de la casa: hoy ApiBase siempre manda string por
      // fail(), pero resuelve tambien el shape objeto que usan otros clients.
      wrap.innerHTML = `<div class="empty-state"><p>Error: ${escHtmlAudit(errorMessage(res))}</p></div>`;
      // Sin esto quedaba en pantalla el grafico del rango ANTERIOR al lado del error, y
      // se lee como si fuera el del rango que acaba de fallar.
      porNombre = new Map(); nombresOrden = []; diasRango = [];
      pintarChart();
      document.getElementById('audit-resumen-clasif').innerHTML = '';
      document.getElementById('audit-detector-aviso').innerHTML = '';
      // TUR-T3: el contador del rango anterior tampoco se queda al lado del error.
      document.getElementById('audit-estimadas').innerHTML = '';
      return;
    }
    const payload = res.data?.data || res.data;
    const items = payload?.items || [];

    // Clasificaciones del detector, indexadas por colaborador+fecha para que la
    // cuadricula las encuentre en O(1) al pintar cada celda.
    const anomPayload = resAnom.ok ? (resAnom.data?.data || resAnom.data) : null;
    const porClasif = new Map();
    for (const a of (anomPayload?.items || [])) {
      porClasif.set(`${a.colaborador}|${(a.fecha || '').slice(0, 10)}`, a);
    }
    const detectorOk = resAnom.ok && porClasif.size > 0;

    renderResumenClasificaciones(
      document.getElementById('audit-resumen-clasif'),
      detectorOk ? anomPayload.resumenPorClasificacion : null
    );

    // FAIL-LOUD (REGLA_DURA_1 del #3795): si el detector no respondio, se dice.
    const $aviso = document.getElementById('audit-detector-aviso');
    if ($aviso) {
      if (resAnom.ok && porClasif.size === 0) {
        $aviso.innerHTML = `<div style="border:1px solid #4a3a1f;border-left:3px solid #d29922;border-radius:6px;padding:9px 12px;background:#0d1117;font-size:0.875rem;color:#d29922">
          El detector respondió sin clasificaciones para este rango. La cuadrícula muestra los estados de asistencia, no las 8 clasificaciones.</div>`;
      } else if (!resAnom.ok) {
        $aviso.innerHTML = `<div style="border:1px solid #5a1f1f;border-left:3px solid #f85149;border-radius:6px;padding:9px 12px;background:#0d1117;font-size:0.875rem;color:#f85149">
          <strong>Sin clasificaciones del detector:</strong> ${escHtmlAudit(errorMessage(resAnom))}<br>
          <span style="color:#8b949e">La cuadrícula quedó con el cálculo local del panel (respaldo), que no distingue “trabajó en su descanso” ni “posible cambio de turno”.</span></div>`;
      } else {
        $aviso.innerHTML = '';
      }
    }

    // El KPI de tardanza NO puede venir del payload: la API suma crudo y ahi van los
    // dias de turno distinto. Es justo el numero que inflo a Brayan a 892 minutos.
    // Se recalcula aca con la misma regla que usan los graficos, para que el titular
    // y el detalle no se contradigan.
    const tardanzaKpi = items.reduce((s, i) => s + tardanzaReal(i), 0);
    const dudososKpi = items.filter(esTurnoDudoso).length;

    // KPIs
    kpis.innerHTML = `
      <div class="ops-att-stat ops-att-stat--ok">
        <div class="ops-att-stat__val" style="color:#3fb950">${payload.diasCompletos || 0}</div>
        <div class="ops-att-stat__label">Dias completos</div>
      </div>
      <div class="ops-att-stat ops-att-stat--warn">
        <div class="ops-att-stat__val" style="color:#d29922">${tardanzaKpi}m</div>
        <div class="ops-att-stat__label">Tardanza real</div>
      </div>
      <div class="ops-att-stat ops-att-stat--err">
        <div class="ops-att-stat__val" style="color:#f85149">${payload.diasIncompletos || 0}</div>
        <div class="ops-att-stat__label">Dias incompletos</div>
      </div>
      <div class="ops-att-stat">
        <div class="ops-att-stat__val" style="color:#b8a0e8">${payload.diasDescanso || 0}</div>
        <div class="ops-att-stat__label">Dias descanso</div>
      </div>
      ${dudososKpi > 0 ? `
      <div class="ops-att-stat">
        <div class="ops-att-stat__val" style="color:#79c0ff">${dudososKpi}</div>
        <div class="ops-att-stat__label">Dias a revisar</div>
      </div>` : ''}
      ${diasConEntradaEstimada(payload) > 0 ? `
      <div class="ops-att-stat" data-audit-kpi-estimadas>
        <div class="ops-att-stat__val" style="color:#d2a8ff">${diasConEntradaEstimada(payload)}</div>
        <div class="ops-att-stat__label">Dias con entrada estimada</div>
      </div>` : ''}`;
    pintarEstimadas(document.getElementById('audit-estimadas'), payload);

    if (items.length === 0) {
      wrap.innerHTML = '<div class="empty-state" style="padding:30px"><p>Sin registros en el rango seleccionado.</p></div>';
      // Sin esto el grafico del rango anterior queda en pantalla junto al "sin
      // registros" y se lee como si fueran los datos del rango nuevo.
      porNombre = new Map(); nombresOrden = []; diasRango = [];
      pintarChart();
      document.getElementById('audit-resumen-clasif').innerHTML = '';
      return;
    }

    // Agrupar por colaborador
    const porColab = new Map();
    for (const it of items) {
      const key = `${it.colaboradorId}|${it.nombreCompleto}|${it.cargo || ''}`;
      if (!porColab.has(key)) porColab.set(key, []);
      porColab.get(key).push(it);
    }

    const dias = rangoDias(desde, hasta);
    diasRango = dias;

    // VIS-T2 (bit #3771): datos para los 2 graficos. Se indexa por NOMBRE (no por la
    // key compuesta) para que el color de cada persona sea estable entre vistas.
    porNombre = new Map();
    for (const it of items) {
      const n = it.nombreCompleto || '(sin nombre)';
      if (!porNombre.has(n)) porNombre.set(n, []);
      porNombre.get(n).push(it);
    }
    nombresOrden = [...porNombre.keys()].sort();
    pintarChart();

    // Header fila
    const headerCells = dias.map(d => `
      <div class="audit-col-dia" style="padding:6px;font-size:0.875rem;color:#8b949e;text-align:center;border-bottom:1px solid #30363d">
        ${labelFechaCorta(d)}
      </div>`).join('');

    const filas = [...porColab.entries()].map(([key, itemsColab]) => {
      const [colabId, nombre, cargo] = key.split('|');
      const porFecha = new Map(itemsColab.map(i => [i.fechaCol?.slice(0, 10), i]));
      const celdas = dias.map(d => {
        // La clasificacion del detector MANDA sobre la heuristica local. celdaEstado
        // solo entra si el detector no cubrio ese dia, y en ese caso el aviso de
        // arriba ya dijo que se esta viendo el respaldo.
        const anom = porClasif.get(`${nombre}|${d}`);
        if (anom) return celdaClasificada(anom);
        const fila = porFecha.get(d);
        return fila ? celdaEstado(fila) : celdaVacia();
      }).join('');
      return `
        <div class="audit-row" style="display:grid;grid-template-columns:220px repeat(${dias.length}, minmax(70px, 1fr));gap:2px;padding:4px 0;border-bottom:1px solid #21262d">
          <div style="padding:4px 8px">
            <div style="font-weight:600;font-size:0.875rem">${nombre}</div>
            ${cargo ? `<div style="font-size:0.875rem;color:#8b949e">${cargo}</div>` : ''}
            ${fmtResumenColab(itemsColab)}
          </div>
          ${celdas}
        </div>`;
    }).join('');

    wrap.innerHTML = `
      <div style="min-width:${220 + dias.length * 70}px">
        <div style="display:grid;grid-template-columns:220px repeat(${dias.length}, minmax(70px, 1fr));gap:2px;background:#0d1117;position:sticky;top:0">
          <div style="padding:8px;font-size:0.875rem;color:#c9d1d9;font-weight:600">Colaborador</div>
          ${headerCells}
        </div>
        ${filas}
      </div>`;
  }

  // Tocar las fechas a mano = rango libre: la plantilla se deduce de la duracion.
  document.getElementById('audit-buscar')?.addEventListener('click', () => aplicarPeriodo('libre'));

  container.querySelectorAll('.audit-periodo-btn').forEach(btn => {
    btn.addEventListener('click', () => aplicarPeriodo(btn.dataset.periodo));
  });

  // VIS-T2 (bit #3771): el toggle solo repinta el grafico -- NO vuelve a pedir datos.
  // Cambiar de lente sobre lo mismo no justifica otra llamada a la API.
  container.querySelectorAll('.audit-vista-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      vistaActiva = btn.dataset.vista;
      container.querySelectorAll('.audit-vista-btn').forEach(b => {
        const activo = b.dataset.vista === vistaActiva;
        b.style.background = activo ? '#1f6feb' : 'transparent';
        b.style.color = activo ? '#fff' : '#8b949e';
      });
      pintarChart();
    });
  });

  // Reactividad al cambio de negocio. Hueco PREEXISTENTE, no lo introduje yo, pero lo
  // cierro porque la inconsistencia estaba en mi propio header: desde aca hay un boton
  // a Asistencia, esa vista SI se re-renderiza al cambiar de negocio y esta NO. Cambiabas
  // de negocio y te quedaba la auditoria del anterior, con su nombre en el banner,
  // mientras el selector global ya decia otro.
  // El cleanup previo se llama ANTES de suscribir, y no es ceremonia.
  // Si el handler re-renderiza la vista, el render vuelve a suscribir y el listener
  // viejo sigue vivo: cada cambio de negocio DUPLICA los listeners. El router solo
  // limpia al REEMPLAZAR la vista (router.js:56), no en un re-render sobre el mismo
  // container. Medido en AsistenciaView, que hace justo eso: 1 -> 2 -> 4 peticiones
  // por cambio de negocio. Con esta linea, esta vista se queda en 1.
  // (ColaboradoresView usa el mismo patron pero NO re-renderiza -- llama loadPage() --
  // asi que no tiene el problema. El caso a corregir es AsistenciaView, reportado aparte.)
  container._cleanupBusinessListener?.();
  function onBusinessChanged() { renderAsistenciaAudit(container); }
  window.addEventListener('business:changed', onBusinessChanged);
  container._cleanupBusinessListener = () => window.removeEventListener('business:changed', onBusinessChanged);

  load();
}
